import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import { Logo, LogoIcon } from "@/components/Logo";
import { Badge } from "@/components/ui/badge";
import { useSigningBranding } from "@/hooks/useSigningBranding";
import { ThemeToggle } from "../ThemeToggle";

export const SigningLayout = () => {
  const { t } = useTranslation();
  const { displayName, showPoweredBy, getLogoUrl } = useSigningBranding();
  const logoUrl = getLogoUrl();

  // Custom org branding wins; the eScribe lockup is only for unbranded
  // sessions, and a custom company name keeps the icon + name arrangement.
  const renderBrandLogo = () => {
    if (logoUrl) {
      return (
        <img
          src={logoUrl}
          alt={displayName}
          className="h-8 max-w-37.5 object-contain"
        />
      );
    }
    if (displayName === "eScribe") {
      return <Logo size="md" showText />;
    }
    return (
      <>
        <LogoIcon size="md" />
        <span className="text-lg font-semibold">{displayName}</span>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      {/* Header */}
      <header className="bg-background border-b sticky top-0 z-50">
        <div className="h-14 px-4 flex items-center justify-between w-full">
          {/* Logo */}
          <div className="flex items-center gap-2">{renderBrandLogo()}</div>

          {/* Right section */}
          <div className="flex items-center gap-3">
            <Badge
              variant="outline"
              className="hidden sm:flex items-center gap-1.5 text-xs font-normal py-1 px-2 bg-success/10 text-success border-success/20"
            >
              <Lock className="h-3 w-3" />
              <span>{t("signing.secureSession")}</span>
            </Badge>
            <ThemeToggle variant="simple" />
          </div>
        </div>
      </header>

      {/* Content - takes remaining space */}
      <main className="flex-1 flex flex-col">
        <Outlet />
      </main>

      {/* Footer - conditionally show powered by */}
      <footer className="bg-background border-t py-3 px-4">
        <p className="text-center text-xs text-muted-foreground">
          {showPoweredBy ? (
            <>
              Powered by eScribe · Electronic signatures that are legally
              binding
            </>
          ) : (
            <>{t("signing.legallyBinding")}</>
          )}
        </p>
      </footer>
    </div>
  );
};

export default SigningLayout;
